import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CalendarIcon as Calendar, PlusIcon as Plus, TrashIcon as Trash, PencilSimpleIcon as PencilSimple, UploadIcon as Upload, XIcon as X, MegaphoneIcon as Megaphone, DownloadIcon as Download } from "@phosphor-icons/react";
import { formatDate, formatApiError, photoUrl } from "@/lib/utils-app";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const empty = {
  title: "",
  event_type: "show",
  description: "",
  location: "",
  start_date: "",
  end_date: "",
  entry_fee: 25,
  cover_image: "",
};

export default function AdminEvents() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [broadcastEvent, setBroadcastEvent] = useState(null);
  const [bMsg, setBMsg] = useState({ title: "", body: "" });
  const [bBusy, setBBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/events").then((r) => setEvents(r.data));

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  if (user && !isAdmin) return <Navigate to="/dashboard" replace />;

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e.id);
    setForm({ ...empty, ...e });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, entry_fee: parseFloat(form.entry_fee) || 0 };
      if (editing) {
        await api.put(`/events/${editing}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/events", payload);
        toast.success("Event created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, cover_image: data.storage_path }));
      toast.success("Cover image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      toast.success("Event deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const sendBroadcast = async () => {
    if (!bMsg.title.trim() || !bMsg.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setBBusy(true);
    try {
      const { data } = await api.post(`/events/${broadcastEvent.id}/broadcast`, bMsg);
      toast.success(`Sent to ${data.recipients} registrant${data.recipients === 1 ? "" : "s"}`);
      setBroadcastEvent(null);
      setBMsg({ title: "", body: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">Admin · Events</p>
          <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">Manage events</h1>
        </div>
        <Button onClick={openNew} className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full px-6" data-testid="admin-new-event-btn">
          <Plus size={16} className="mr-1" /> New event
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit event" : "New event"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Title *</Label>
                <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-lg" data-testid="event-title-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Type *</Label>
                <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="event-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="show">Show / conformation</SelectItem>
                    <SelectItem value="sport">Sport (agility, obedience…)</SelectItem>
                    <SelectItem value="health_clinic">Health clinic</SelectItem>
                    <SelectItem value="meetup">Meetup</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Entry fee (USD)</Label>
                <Input type="number" min="0" step="0.01" value={form.entry_fee} onChange={(e) => setForm({ ...form, entry_fee: e.target.value })} className="mt-1 rounded-lg" data-testid="event-fee-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Start date *</Label>
                <Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 rounded-lg" data-testid="event-start-input" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">End date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1 rounded-lg" data-testid="event-end-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Location *</Label>
                <Input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1 rounded-lg" data-testid="event-location-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Description</Label>
                <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-lg" data-testid="event-desc-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Cover image</Label>
                <div className="mt-1 flex items-center gap-3">
                  {form.cover_image ? (
                    <div className="relative w-32 h-20 rounded-lg overflow-hidden bg-[#FAFAFA]">
                      <img src={photoUrl(form.cover_image)} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, cover_image: "" }))}
                        className="absolute top-1 right-1 bg-white/90 rounded-full p-1 hover:bg-white"
                        data-testid="event-remove-cover"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <label className="w-32 h-20 border-2 border-dashed border-[#E5E5E5] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-[#FAFAFA] transition-colors">
                      <Upload size={20} className="text-[#6B6B6B] mb-1" />
                      <span className="text-xs text-[#6B6B6B]">Upload</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={onCoverUpload}
                        data-testid="event-cover-input"
                      />
                    </label>
                  )}
                  <p className="text-xs text-[#6B6B6B]">Optional. Recommended 16:9 ratio.</p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
              <Button type="submit" className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full" data-testid="event-save-btn">
                {editing ? "Save changes" : "Create event"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!broadcastEvent} onOpenChange={(o) => !o && setBroadcastEvent(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <span className="flex items-center gap-2">
                <Megaphone size={20} weight="duotone" className="text-crimson" />
                Broadcast to {broadcastEvent?.title} registrants
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#6B6B6B]">
              Sends an inbox message to every user who registered a dog for this event.
            </p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Subject *</Label>
              <Input
                value={bMsg.title}
                onChange={(e) => setBMsg({ ...bMsg, title: e.target.value })}
                placeholder="e.g. Ring numbers posted"
                className="mt-1 rounded-lg"
                data-testid="broadcast-title-input"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">Message *</Label>
              <Textarea
                rows={5}
                value={bMsg.body}
                onChange={(e) => setBMsg({ ...bMsg, body: e.target.value })}
                placeholder="Schedule changes, judging order, parking info…"
                className="mt-1 rounded-lg"
                data-testid="broadcast-body-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setBroadcastEvent(null)} className="rounded-full">Cancel</Button>
            <Button
              onClick={sendBroadcast}
              disabled={bBusy}
              className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full"
              data-testid="broadcast-send-btn"
            >
              {bBusy ? "Sending…" : "Send broadcast"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {events.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-16 text-center">
          <Calendar size={48} weight="duotone" className="text-ink mx-auto mb-4" />
          <h3 className="font-spectral text-2xl mb-2">No events yet</h3>
          <p className="text-[#6B6B6B] mb-4">Create your first event to start collecting registrations.</p>
          <Button onClick={openNew} className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full px-6">Create event</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {events.map((e) => (
            <div key={e.id} className="bg-white border border-[#E5E5E5] rounded-xl p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid={`admin-event-card-${e.id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">{e.event_type}</span>
                  <h3 className="font-spectral text-2xl tracking-tight mt-1">{e.title}</h3>
                  <p className="text-sm text-[#6B6B6B] mt-1">{e.location}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="font-spectral text-xl">${e.entry_fee.toFixed(0)}</span>
                </div>
              </div>
              <div className="text-xs text-[#6B6B6B] mb-4">
                {formatDate(e.start_date)}{e.end_date && e.end_date !== e.start_date && ` — ${formatDate(e.end_date)}`}
              </div>
              <div className="flex gap-2">
                <Link to={`/events/${e.id}`} className="flex-1">
                  <Button variant="outline" className="w-full rounded-full border-[#E5E5E5]">View public</Button>
                </Link>
                <a
                  href={`${BACKEND}/api/events/${e.id}/registrations.csv?auth=${encodeURIComponent(localStorage.getItem("token") || "")}`}
                  download
                  data-testid={`export-csv-${e.id}`}
                >
                  <Button type="button" variant="outline" size="icon" className="rounded-full border-[#E5E5E5]" title="Export registrants CSV">
                    <Download size={14} />
                  </Button>
                </a>
                <Button
                  onClick={() => { setBroadcastEvent(e); setBMsg({ title: "", body: "" }); }}
                  variant="outline"
                  size="icon"
                  className="rounded-full border-[#E5E5E5]"
                  title="Broadcast a message to registrants"
                  data-testid={`broadcast-event-${e.id}`}
                >
                  <Megaphone size={14} />
                </Button>
                <Button onClick={() => openEdit(e)} variant="outline" size="icon" className="rounded-full border-[#E5E5E5]" data-testid={`edit-event-${e.id}`}>
                  <PencilSimple size={14} />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="icon" className="rounded-full border-[#C1272D]/30 text-crimson hover:bg-[#C1272D]/5" data-testid={`delete-event-${e.id}`}>
                      <Trash size={14} />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {e.title}?</AlertDialogTitle>
                      <AlertDialogDescription>All registrations for this event will be deleted.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => onDelete(e.id)}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
